import _ from 'lodash';
import moment from 'moment';

import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import {
  createEnumOptions,
  getEnumTranslationKey,
  getEnumValue,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { formatTZLocal, getEnumNamespace } from '@/utils/infrastructure';
import Filters, { registerModuleFilters } from '@/utils/input/filters';
import { formatCurrency } from '@/utils/output/formatters';

import { CLAIM_IDEMPOTENCY_STORAGE_PREFIX, MANUAL_CLAIM_PROVIDERS, MODEL_NAME } from './constants';

const { BaseFilters } = Filters;

const { BillingCollectionMode, BillingPaymentTransactionStatus, BillingSubscriptionStatus, ChannelBillingMode } =
  CoreEnums;

const CLAIM_ELIGIBLE_STATUSES = new Set([
  BillingSubscriptionStatus.PendingActivation,
  BillingSubscriptionStatus.PendingPayment,
  BillingSubscriptionStatus.PastDue,
]);

const MANUAL_CLAIM_PROVIDER_VALUES = new Set([
  CoreEnums.BillingPaymentProviderType.BankTransfer,
  CoreEnums.BillingPaymentProviderType.Manual,
  MANUAL_CLAIM_PROVIDERS.BANK_TRANSFER,
  MANUAL_CLAIM_PROVIDERS.MANUAL,
]);

export const unwrapPaymentList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const resolvePaymentStatus = (payment) =>
  parseOptionalNumericEnumFromApi(BillingPaymentTransactionStatus, payment?.status);

export const isPendingManualClaim = (payment) =>
  resolvePaymentStatus(payment) === BillingPaymentTransactionStatus.Pending;

export const isPaidPayment = (payment) =>
  resolvePaymentStatus(payment) === BillingPaymentTransactionStatus.Paid;

export const isCancelledPayment = (payment) =>
  resolvePaymentStatus(payment) === BillingPaymentTransactionStatus.Cancelled;

/** Reported claim facts are locked after a successful Pending submit. */
export const areReportedFactsLocked = (payment) =>
  isPendingManualClaim(payment) || isPaidPayment(payment) || isCancelledPayment(payment);

/** Confirmed Paid snapshot is fully historical. */
export const isConfirmedSnapshotLocked = (payment) => isPaidPayment(payment);

export const isManualBillingSubscription = (subscription) => {
  if (!subscription) return false;
  const billingMode = parseOptionalNumericEnumFromApi(ChannelBillingMode, subscription.billingMode);
  const collectionMode = parseOptionalNumericEnumFromApi(
    BillingCollectionMode,
    subscription.paymentCollectionMode,
  );
  return (
    billingMode === ChannelBillingMode.ManualInvoice ||
    collectionMode === BillingCollectionMode.Manual
  );
};

export const isClaimEligibleSubscriptionStatus = (subscription) => {
  const status = parseOptionalNumericEnumFromApi(
    BillingSubscriptionStatus,
    subscription?.status,
  );
  return CLAIM_ELIGIBLE_STATUSES.has(status);
};

/** CTA: manual collection/invoice + eligible lifecycle status; hide when a Pending claim exists. */
export const canSubmitManualPaymentClaim = (subscription, payments = []) => {
  if (!isManualBillingSubscription(subscription)) return false;
  if (!isClaimEligibleSubscriptionStatus(subscription)) return false;
  if (Array.isArray(payments) && payments.some(isPendingManualClaim)) return false;
  // Attention / subscription snapshot when payment history is not loaded yet.
  const lastPaymentStatus = parseOptionalNumericEnumFromApi(
    BillingPaymentTransactionStatus,
    subscription?.lastPaymentStatus,
  );
  if (lastPaymentStatus === BillingPaymentTransactionStatus.Pending) return false;
  return true;
};

export const findLatestPendingClaim = (payments = []) => payments.find(isPendingManualClaim) || null;

export const findLatestPayment = (payments = []) => (Array.isArray(payments) ? payments[0] : null);

export const resolveManualClaimProviderApiValue = (raw) => {
  if (raw == null || raw === '') return MANUAL_CLAIM_PROVIDERS.BANK_TRANSFER;
  if (typeof raw === 'object') {
    return resolveManualClaimProviderApiValue(raw.value ?? raw.id ?? raw.key);
  }
  const resolved = parseOptionalNumericEnumFromApi(CoreEnums.BillingPaymentProviderType, raw);
  if (resolved === CoreEnums.BillingPaymentProviderType.Manual) {
    return MANUAL_CLAIM_PROVIDERS.MANUAL;
  }
  if (resolved === CoreEnums.BillingPaymentProviderType.BankTransfer) {
    return MANUAL_CLAIM_PROVIDERS.BANK_TRANSFER;
  }
  const kebab = _.kebabCase(String(raw));
  if (kebab === MANUAL_CLAIM_PROVIDERS.MANUAL) return MANUAL_CLAIM_PROVIDERS.MANUAL;
  return MANUAL_CLAIM_PROVIDERS.BANK_TRANSFER;
};

export const isManualClaimProvider = (provider) => {
  const resolved = parseOptionalNumericEnumFromApi(CoreEnums.BillingPaymentProviderType, provider);
  if (resolved !== undefined) {
    return (
      resolved === CoreEnums.BillingPaymentProviderType.BankTransfer ||
      resolved === CoreEnums.BillingPaymentProviderType.Manual
    );
  }
  return MANUAL_CLAIM_PROVIDER_VALUES.has(provider) || MANUAL_CLAIM_PROVIDER_VALUES.has(_.kebabCase(String(provider || '')));
};

/**
 * Stable client idempotency key per subscription for identical retries.
 * Survives remounts within the same browser session.
 */
export const getOrCreateClaimIdempotencyKey = (subscriptionId) => {
  if (!subscriptionId || typeof window === 'undefined') {
    return `manual-claim-${subscriptionId || 'unknown'}`;
  }
  const storageKey = `${CLAIM_IDEMPOTENCY_STORAGE_PREFIX}${subscriptionId}`;
  try {
    const existing = window.sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const next = `manual-claim-${subscriptionId}`;
    window.sessionStorage.setItem(storageKey, next);
    return next;
  } catch {
    return `manual-claim-${subscriptionId}`;
  }
};

/** After a successful distinct claim, rotate the key so a new claim is allowed later. */
export const clearClaimIdempotencyKey = (subscriptionId) => {
  if (!subscriptionId || typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(`${CLAIM_IDEMPOTENCY_STORAGE_PREFIX}${subscriptionId}`);
  } catch {
    /* ignore */
  }
};

export const formatPaymentAmount = (amount, currency) => {
  if (amount == null || amount === '') return DEFAULT_EMPTY_VALUE;
  const isoCurrency = getEnumValue(CoreEnums.Currency, currency) || 'USD';
  return formatCurrency(amount, isoCurrency, { maximumFractionDigits: 2 });
};

export const formatPaymentDate = (value) => (value ? formatTZLocal(value) : DEFAULT_EMPTY_VALUE);

export const formatPaymentProviderLabel = (provider, formatMessage) => {
  const resolved = parseOptionalNumericEnumFromApi(CoreEnums.BillingPaymentProviderType, provider);
  if (resolved === undefined) {
    return provider || DEFAULT_EMPTY_VALUE;
  }
  return formatMessage({
    id: getEnumTranslationKey(
      CoreEnums.BillingPaymentProviderType,
      resolved,
      'billingPaymentProviderType',
    ),
  });
};

export const formatPaymentStatusLabel = (status, formatMessage) => {
  const resolved = parseOptionalNumericEnumFromApi(BillingPaymentTransactionStatus, status);
  if (resolved === undefined) return status || DEFAULT_EMPTY_VALUE;
  return formatMessage({
    id: getEnumTranslationKey(
      BillingPaymentTransactionStatus,
      resolved,
      'billingPaymentTransactionStatus',
    ),
  });
};

export const hasReportedGrossDiscrepancy = (payment, confirmedGross) => {
  if (payment?.reportedGrossAmount == null || confirmedGross == null || confirmedGross === '') {
    return false;
  }
  return Number(payment.reportedGrossAmount) !== Number(confirmedGross);
};

export const buildClaimSubmitPayload = ({
  companyBillingSubscriptionId,
  provider,
  grossAmount,
  reportedPaymentDate,
  externalReference,
  evidenceDocumentId,
  notes,
  idempotencyKey,
}) => {
  const payload = {
    companyBillingSubscriptionId,
    provider: resolveManualClaimProviderApiValue(provider),
    grossAmount: Number(grossAmount),
    reportedPaymentDate,
    externalReference: String(externalReference || '').trim(),
    idempotencyKey: String(idempotencyKey || '').trim(),
  };
  const trimmedNotes = String(notes || '').trim();
  if (trimmedNotes) payload.notes = trimmedNotes;
  const evidence = String(evidenceDocumentId || '').trim();
  if (evidence) payload.evidenceDocumentId = evidence;
  return payload;
};

export const buildConfirmPayload = ({
  netAmount,
  taxAmount,
  grossAmount,
  effectivePaymentDate,
  externalReference,
  providerTaxReference,
  acknowledgeReportedGrossDiscrepancy,
}) => {
  const payload = {
    netAmount: Number(netAmount),
    taxAmount: Number(taxAmount),
    grossAmount: Number(grossAmount),
    effectivePaymentDate,
    acknowledgeReportedGrossDiscrepancy: Boolean(acknowledgeReportedGrossDiscrepancy),
  };
  const reference = String(externalReference || '').trim();
  if (reference) payload.externalReference = reference;
  const taxRef = String(providerTaxReference || '').trim();
  if (taxRef) payload.providerTaxReference = taxRef;
  return payload;
};

/** Never coalesce reported and confirmed references for display. */
export const getReportedExternalReference = (payment) =>
  payment?.reportedExternalReference ?? null;

export const getConfirmedExternalReference = (payment) => payment?.externalReference ?? null;

const ATTENTION_SUBSCRIPTION_STATUSES = new Set([
  BillingSubscriptionStatus.PendingActivation,
  BillingSubscriptionStatus.PendingPayment,
  BillingSubscriptionStatus.PastDue,
]);

const subscriptionStatusBadge = {
  [BillingSubscriptionStatus.PendingActivation]: { status: 'info' },
  [BillingSubscriptionStatus.PendingPayment]: { status: 'warning' },
  [BillingSubscriptionStatus.PastDue]: { status: 'execution' },
};

const paymentStatusBadge = {
  [BillingPaymentTransactionStatus.Pending]: { status: 'processing', type: 'process' },
  [BillingPaymentTransactionStatus.RequiresAction]: { status: 'default', type: 'process' },
  [BillingPaymentTransactionStatus.Processing]: { status: 'info', type: 'process' },
  [BillingPaymentTransactionStatus.Paid]: { status: 'approved', type: 'process' },
  [BillingPaymentTransactionStatus.Failed]: { status: 'error', type: 'process' },
  [BillingPaymentTransactionStatus.Cancelled]: { status: 'rejected', type: 'process' },
};

const StatusFilter = {
  ...BaseFilters.EnumFilter,
  name: 'status',
  label: 'global.text.status',
  enumNamespace: getEnumNamespace(BillingSubscriptionStatus, 'Billing'),
  enumSource: BillingSubscriptionStatus,
  options: createEnumOptions(BillingSubscriptionStatus, 'billingSubscriptionStatus', {
    casing: 'camel',
  }).filter((option) => ATTENTION_SUBSCRIPTION_STATUSES.has(option.value)),
  badgeMap: subscriptionStatusBadge,
  badgeType: 'group-item',
  filterSearch: true,
};

const LastPaymentStatusFilter = {
  ...BaseFilters.EnumFilter,
  name: 'lastPaymentStatus',
  label: 'billingSubscriptionPayments.columns.lastPaymentStatus',
  enumNamespace: getEnumNamespace(BillingPaymentTransactionStatus, 'Billing'),
  enumSource: BillingPaymentTransactionStatus,
  options: createEnumOptions(BillingPaymentTransactionStatus, 'billingPaymentTransactionStatus', {
    casing: 'camel',
  }),
  badgeMap: paymentStatusBadge,
  badgeType: 'process',
  filterSearch: true,
};

const CompanyFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'companyId',
  label: 'billingSubscriptionPayments.columns.company',
  labelInValue: true,
  displayField: 'commercialName',
  apiRoute: 'company/paged',
  filterSearch: true,
  params: {
    select: 'id,commercialName',
    filter: 'isActive eq true',
    orderBy: 'commercialName asc',
  },
  oDataField: 'companyId',
};

const DueDateFilter = {
  ...Filters.DateFilter,
  name: 'dueDate',
  label: 'billingSubscriptionPayments.columns.dueDate',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

const buildPlanFilter = (plansPagedRoute) => {
  if (!plansPagedRoute) return null;
  return {
    ...BaseFilters.RemoteFilter,
    name: 'planId',
    label: 'billingSubscriptionPayments.columns.plan',
    labelInValue: true,
    displayField: 'name',
    apiRoute: plansPagedRoute,
    filterSearch: true,
    params: {
      select: 'id,name',
      filter: 'isActive eq true',
      orderBy: 'name asc',
    },
    oDataField: 'planId',
  };
};

/** Build list filters; `plansPagedRoute` comes from commercial-billing session navigation. */
export const buildFiltersConfig = (plansPagedRoute) =>
  [
    StatusFilter,
    LastPaymentStatusFilter,
    CompanyFilter,
    buildPlanFilter(plansPagedRoute),
    DueDateFilter,
  ].filter(Boolean);

/** Default (no plan remote until session navigation resolves plans/paged). */
export const filtersConfig = buildFiltersConfig(null);

registerModuleFilters(MODEL_NAME, filtersConfig);

const unwrapFilterValue = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object' && raw !== null && 'value' in raw) return raw.value;
  return raw;
};

const matchesEnumFilter = (rawItemValue, filterValue, enumSource) => {
  if (filterValue == null || filterValue === '') return true;
  const resolvedItem = parseOptionalNumericEnumFromApi(enumSource, rawItemValue);
  const resolvedFilter = parseOptionalNumericEnumFromApi(enumSource, filterValue);
  if (resolvedItem === undefined || resolvedFilter === undefined) {
    return String(rawItemValue) === String(filterValue);
  }
  return resolvedItem === resolvedFilter;
};

const matchesDueDateFilter = (item, dueDateFilter) => {
  if (dueDateFilter == null || dueDateFilter === CoreEnums.FilterDateRange.Disabled) return true;
  if (!Array.isArray(dueDateFilter) || dueDateFilter.length < 2) return true;
  if (!item?.dueDate) return false;
  const [start, end] = dueDateFilter;
  const due = moment(item.dueDate);
  if (!due.isValid()) return false;
  const startMoment = moment(start);
  const endMoment = moment(end);
  if (!startMoment.isValid() || !endMoment.isValid()) return true;
  return (
    due.isSameOrAfter(startMoment.clone().startOf('day')) &&
    due.isSameOrBefore(endMoment.clone().endOf('day'))
  );
};

const matchesSearch = (item, search) => {
  const q = String(search || '')
    .trim()
    .toLowerCase();
  if (!q) return true;
  const haystack = [
    item?.companyName,
    item?.planName,
    item?.lastReportedExternalReference,
    item?.lastConfirmedExternalReference,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

/**
 * Client-side filter for the attention queue (full list, not OData paged).
 * Keeps UX working even when GET .../attention ignores $filter.
 */
export const applyAttentionListFilters = (items = [], moduleFilters = {}, search) => {
  const list = Array.isArray(items) ? items : [];
  const filters = moduleFilters && typeof moduleFilters === 'object' ? moduleFilters : {};

  return list.filter((item) => {
    if (!matchesSearch(item, search)) return false;

    const companyId = unwrapFilterValue(filters.companyId);
    if (companyId) {
      const itemCompanyId = item.companyId ?? item.company?.id;
      if (itemCompanyId == null || String(itemCompanyId) !== String(companyId)) return false;
    }

    const planId = unwrapFilterValue(filters.planId);
    if (planId) {
      const itemPlanId = item.planId || item.automyPlanId || item.plan?.id;
      if (String(itemPlanId) !== String(planId)) return false;
    }

    if (!matchesEnumFilter(item.status, filters.status, BillingSubscriptionStatus)) return false;
    if (
      !matchesEnumFilter(
        item.lastPaymentStatus,
        filters.lastPaymentStatus,
        BillingPaymentTransactionStatus,
      )
    ) {
      return false;
    }

    if (!matchesDueDateFilter(item, filters.dueDate)) return false;

    return true;
  });
};
